import { getPlayerVersion } from "@/shared/Utils/getPlayerVersion";
import {
  isLocaleSwitcherEnabled,
  isRuntimeLocalesEnabled,
} from "@/locales/runtimeLocaleFlags";

const NAMESPACE_PRELOAD_TIMEOUT_MS = 2_000;

/*
 * Both runtime-locale flags off — the state of every user until one is set — so
 * the boot path stays exactly what it was before AT2-19905: one dynamic import,
 * no manifest request, no extra chunk hop. The flag readers come from a leaf
 * module (runtimeLocaleFlags → stickyFlag) precisely so this decision costs the
 * entry chunk nothing.
 */
if (!isLocaleSwitcherEnabled() && !isRuntimeLocalesEnabled()) {
  import("./bootstrap");
} else {
  /*
   * The published-locale set has to be in place before the bootstrap chunk is
   * evaluated: bootstrap reaches i18n.ts through App → I18nProvider, and i18n.ts
   * resolves getAppLocale() at module eval. A failed or slow manifest fetch
   * resolves anyway (see locales/publishedLocales.ts) — boot is never blocked.
   *
   * i18n.ts is then imported here rather than left to bootstrap so this build's
   * namespaces can be awaited before the first paint. For a locale outside
   * src/locales/{en,uk} they load from the CDN, and painting without them shows
   * English from FALLBACK_CHAIN and then re-renders. Importing it earlier also
   * keeps the init-order contract with the MF remotes (i18next initialised
   * before any remote loads) — bootstrap's own import reuses this same module.
   */
  void (async () => {
    const { loadPublishedLocales } = await import("./locales/publishedLocales");

    await loadPublishedLocales();

    const { getAppLocale, getBuildLocale } =
      await import("./locales/getAppLocale");
    const { default: i18n, CRITICAL_NAMESPACES } =
      await import("./locales/i18n");

    // Only a non-build runtime locale needs its criticals before first paint —
    // the build locale's are bundled. The guard is load-bearing, not an
    // optimization: i18next's loadResources expands every loadNamespaces call
    // over the FULL fallback chain (toResolveHierarchy), so running this
    // unconditionally fetches the fallback locale's copies on every flag-on
    // boot. CRITICAL_NAMESPACES, never the dev-wide bundle list — dev and prod
    // must fetch identically (spec Resolved #7). After a switch the set is
    // warm from the switch-time prefetch, so the race resolves from IndexedDB.
    //
    // date-fns words load in the same window: getDateFnsLocale is synchronous
    // (formatDate, the date pickers — incl. module-scope captures), so the
    // words must be registered before the bootstrap chunk evaluates. The
    // loader never rejects and carries its own wait cap
    // (ENG_LOCALE_DATA_TIMEOUT_MS), so this await cannot hold the first paint
    // on a hung chunk request. Bundled locales short-circuit.
    if (getAppLocale() !== getBuildLocale()) {
      const { loadDateFnsLocale } = await import("./locales/localeFormatters");

      await Promise.all([
        loadDateFnsLocale(),
        Promise.race([
          i18n.loadNamespaces(CRITICAL_NAMESPACES),
          new Promise(resolve =>
            setTimeout(resolve, NAMESPACE_PRELOAD_TIMEOUT_MS),
          ),
        ]),
      ]);
    }

    await import("./bootstrap");
  })();
}

if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/cache-worker.js")
    .then(reg => {
      if (navigator.serviceWorker.controller) {
        getPlayerVersion().then(version => {
          navigator.serviceWorker.controller?.postMessage({
            type: "SET_VERSION",
            version,
          });
        });
      }
      if (reg.waiting) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    })
    .catch(error => {
      console.error("[Service Worker] Registration failed:", error);
    });
}
